import { SlateAutoSizeInput } from '../../../../SlateAudoSizeInput/SlateAutoSizeInput';
import { leftPad } from '@doltech/utils/lib/number';
import { isNotEmpty } from '@doltech/utils/lib/text';
import { get, isEmpty } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { RenderElementProps } from 'slate-react';
import styled from 'styled-components';
import { colorsV2 } from '../../../../colors-v2';
import { Box } from '../../../../Common/BoxV2';
import { Typography } from '../../../../DesignSystemV2/Typography';
import { findBellowByType } from '../../../../DolEditor/helpers/nodeValue';
import { GapFillWithHintBlankV2 } from '../../../plugins/ExerciseGapFillWithHint/components/readonly/GapFillWithHintBlankV2';
import { EXERCISE_GAP_FILL_WITH_HINT_QUESTION_CONTENT } from '../../../plugins/ExerciseGapFillWithHint/types';
import { CORRECT_ANSWER } from '../../CorrectAnswers/types';
import { useBlankV2DoTestStore } from '../store/do-test.store';
import { BLANK_V2 } from '../types';
import { useRootElement } from '../../../../../slatePlugins/common/hocs/current-element.store';

const resultStyle = {
  lineHeight: 1.2,
  display: 'inline-block',
};

export const ReadOnlyBlankV2ElementBase = observer((props: RenderElementProps) => {
  const { attributes, children, element } = props;
  const store = useBlankV2DoTestStore();
  const rootElement = useRootElement();

  // TODO: [DynamicImport] temporary fix to bypass store not initialized when element loaded async
  if (!store) {
    return null;
  }

  const entries = findBellowByType(rootElement, CORRECT_ANSWER).filter(
    (n) => n.forId === element.id
  );

  const blanks = findBellowByType(rootElement, BLANK_V2);
  const blankOrder =
    blanks.findIndex((blank) => blank.id === element.id) + (rootElement.startFrom ?? 1);

  const userAnswer = store.userAnswers.get(element.id)?.userAnswer ?? '';
  const correctAnswer = get(entries, '0.correctAnswer', '');
  const isCorrect =
    isNotEmpty(userAnswer) &&
    correctAnswer
      .split('/')
      .map((ans) => ans.trim().toLowerCase())
      .some((ans) => ans === userAnswer.toLowerCase());
  const isMissing = isEmpty(userAnswer) && !isCorrect;

  const renderResult = React.useCallback(() => {
    if (isMissing) {
      return (
        <span>
          <Typography.Text
            variant="regular/20"
            ml="8px"
            color="green100"
            style={{
              borderBottom: `1px solid ${colorsV2.gray40}`,
              ...resultStyle,
            }}
          >
            {correctAnswer}
          </Typography.Text>
        </span>
      );
    }
    if (!isCorrect) {
      return (
        <span>
          <Typography.Text
            variant="regular/20"
            color="red100"
            ml="8px"
            style={{
              borderBottom: `1px solid ${colorsV2.red100}`,
              textDecoration: 'line-through',
              ...resultStyle,
            }}
          >
            {userAnswer}
          </Typography.Text>
          <Typography.Text
            variant="regular/20"
            ml="8px"
            color="green100"
            style={{
              borderBottom: `1px solid ${colorsV2.green80}`,
              ...resultStyle,
            }}
          >
            {correctAnswer}
          </Typography.Text>
        </span>
      );
    }
    return (
      <span>
        <Typography.Text
          variant="regular/20"
          ml="8px"
          color="green100"
          style={{
            borderBottom: `1px solid ${colorsV2.green80}`,
            ...resultStyle,
          }}
        >
          {userAnswer}
        </Typography.Text>
      </span>
    );
  }, [correctAnswer, isCorrect, isMissing, userAnswer]);

  return (
    <Box
      display="inline"
      contentEditable={false}
      borderRadius="8px"
      data-slate-zero-width
      mx="8px"
      px="2px"
      py="4px"
      {...attributes}
    >
      <Box height="32px" display="inline-block">
        <Typography.Text variant="semi-bold/20" color="blue100" mr="8px">
          {`${leftPad(blankOrder, 2)}.`}
        </Typography.Text>

        {store.isViewResult ? (
          renderResult()
        ) : (
          <Input
            value={userAnswer}
            onChange={(newValue) => {
              store.update({ id: element.id, userAnswer: newValue });
            }}
          />
        )}
      </Box>
      {children}
    </Box>
  );
});

export const ReadOnlyBlankV2Element = (props: RenderElementProps) => {
  const { element } = props;

  switch (element.parentNodeType) {
    case EXERCISE_GAP_FILL_WITH_HINT_QUESTION_CONTENT: {
      return <GapFillWithHintBlankV2 {...props} />;
    }
    default:
  }

  return <ReadOnlyBlankV2ElementBase {...props} />;
};

const Input = styled(SlateAutoSizeInput)`
  > * {
    color: ${colorsV2.black100};
    min-width: 60px;
    border-bottom: 2px solid ${colorsV2.gray100};
    line-height: 30px;

    &:focus {
      border-bottom: 2px solid ${colorsV2.blue80};
    }

    &::selection {
      background: ${colorsV2.blue40};
      color: ${colorsV2.black100};
    }
  }
`;
